import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Input, Button, Typography, Card } from 'antd';
import { resetPassword } from '../config/api';
import { Encrypt } from '../utils/encryption';
import forgottenbrain from '../assets/forgottenbrain.gif';
import '../styles/ResetPassword.css';
import axios from 'axios';

const { Title } = Typography;

const ResetPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const email = Form.useWatch('email', form);
  const [loading, setLoading] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false); // 👈 Track if we show the code input
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isEmailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState(''); // 👈 Code input state
  const [responseMessage, setResponseMessage] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);

  const emailInputRef = useRef(null);  // Reference to the email input field

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);


  const handleVerifyClick = async (action='') => {
    if (isEmailVerified && action !== 'Resend') {
      setEmailVerified(false); // Let user re-edit
      setShowCodeInput(false); // Optionally hide the code box
      return;
    }
    if (action === 'Resend') {
      setVerificationCode('');
      form.setFieldsValue({ verificationCode: '' });
      setEmailVerified(false); // Resend to same Email
    }

    setLoading(true);

    try {
      const response = await axios.post(resetPassword, Encrypt(email), {
        headers: { 'Content-Type': 'application/json' },
      });
      const token = response.data?.token;
      
      if (token) {
        setShowCodeInput(true); // 👈 Show input field for verification code
        setVerificationToken(token);
        setEmailVerified(true);
        form.setFieldsValue({ userName: email });
        setResendCountdown(46); // ⏱ Start countdown 45 Seconds
      }
      
    }catch (error) {
      console.error('Token generation failed:', error);

    }finally {
      setLoading(false);

    }
  };

  const onFinish = async (values) => {
    setFinalLoading(true);

    const payload = {
      action: 'reset',
      token: verificationToken,
      ...values
    };

    try {
      const response = await axios.post(resetPassword, Encrypt(payload), 
        {
          headers: { 
            'Content-Type': 'application/json' 
          },
        }
      );
      setResponseMessage(`${response.data?.message || '🤔Something is not right'}`);
      // ✅ Redirect if password reset is successful
      if (response.status === 200 && response.data?.message.toLowerCase().includes('success')) {
        setTimeout(() => {
          navigate('/login', {replace: true}); // 🔁 redirect to login page
        }, 1600); // Optionally delay so user sees success message
      }

    }catch (error) {
      console.error('Reset error:', error);
      setResponseMessage(`🤔${error.response?.data?.message || '🤔Something is not right'}`);

    }finally {
      setFinalLoading(false);
    }
  };

  // Checks if the register button should be enabled
  const isFormValid = () => {
    const values = form.getFieldsValue(['email', 'userName']);
    return values.email && values.userName;
  };

  return (
    <div className="reset-container">
      <div className="Reset-logo-container">
        <img src={forgottenbrain} alt="MindBank Logo" className="Reset-brain-logo" />
      </div>
      <Title level={2}>MindBank Password Reset</Title>

      <Form
        form={form}
        name='resetPassword'
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        className="register-form"
      >
        <Form.Item
          label="Registered Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <div className="email-input-wrapper" style={{ position: 'relative' }}>
            <Input
              type="email"
              value={email}
              onChange={(e) => form.setFieldsValue({ email: e.target.value })}
              ref={emailInputRef}
              disabled={isEmailVerified}
              className="email-input"
              style={{ paddingRight: 100 }} // Leave space for button
            />
              <Button
                type="primary"
                className="inside-email-button"
                onClick={()=>handleVerifyClick('Edit')}
                disabled={loading || !isValidEmail(email)}
                loading={loading}
              >
                {loading
                  ? 'Verifying...'
                  : isEmailVerified
                  ? 'Edit'
                  : 'Verify'
                }
              </Button>
          </div>
      
          {showCodeInput && (
            <div className="inline-verification-block">
              <Form.Item
                name="verificationCode"
                noStyle
                rules={[{ required: true, message: 'Verification code is required' }]}
              >
                <Input
                  placeholder="Verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                />
              </Form.Item>
                <Button
                  type="link"
                  onClick={()=>handleVerifyClick('Resend')}
                  disabled={resendCountdown > 0}
                >
                {resendCountdown > 0 ? `↻ Resend (⏱ ${resendCountdown}s)` : '↻ Resend'}
                  </Button>
            </div>
          )}
                  
        </Form.Item>

        {/* Auto-populated user name */}
        <Form.Item
          label="User Name (Auto-populated)"
          name="userName"
          rules={[{ required: true, message: 'User name is required' }]}
        >
          <Input disabled />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: 'Please enter a password' },
                { min: 4, message: 'Must be 4 characters or more' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={finalLoading} disabled={!isFormValid()}
          >
            {finalLoading ? 'Changing Password...' : 'Reset Password'}
          </Button>
        </Form.Item>
      </Form>

      {responseMessage && (
        <Card title="Status" style={{ marginTop: 24 }}>
          <pre>{responseMessage}</pre>
        </Card>
      )}
    </div>
  );
};

export default ResetPassword;
